import { suggestKits } from "@cmh/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { errorMessage, get, post, type Catalog, type Migration } from "../../lib/api";
import { formatBytes, KIND_LABELS } from "../../lib/format";
import { uploadCsv } from "../../lib/upload";
import { Alert, Badge, Button, Card, Field, Input, Loading, PageHeader, Select } from "../../components/ui";
import { useCatalog, useConnections, useFiles, useWorkspaceId } from "./hooks";

type Kit = Catalog["kits"][number];

export function NewMigrationPage() {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { fileId: presetFile } = useSearch({ strict: false }) as { fileId?: string };
  const catalog = useCatalog();
  const files = useFiles(ws);
  const conns = useConnections(ws);
  const [kit, setKit] = useState<Kit | null>(null);
  const [entity, setEntity] = useState<string>("");
  const [fileId, setFileId] = useState<string>(presetFile ?? "");
  const [sourceConn, setSourceConn] = useState("");
  const [sourceObj, setSourceObj] = useState("");
  const [targetConn, setTargetConn] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const readyFiles = (files.data ?? []).filter((f) => f.status === "ready");
  const selectedFile = readyFiles.find((f) => f.id === fileId);
  const suggestions = useMemo(() => (selectedFile?.sniff ? suggestKits(selectedFile.sniff.headers) : []), [selectedFile]);
  const csvConn = (conns.data ?? []).find((c) => c.kind === "csv");
  const objects = useQuery({
    queryKey: ["objects", ws, sourceConn],
    enabled: Boolean(kit && kit.source !== "csv" && sourceConn),
    queryFn: async () => (await get<{ objects: Array<{ id: string; name: string; count?: number }> }>(`/api/workspaces/${ws}/connections/${sourceConn}/objects`)).objects,
  });

  if (catalog.isLoading || files.isLoading || conns.isLoading) return <Loading />;
  const kits = catalog.data!.kits;

  const pickKit = (k: Kit, ent?: string) => {
    setKit(k);
    setEntity(ent ?? k.entities[0]!.entity);
    setTargetConn((conns.data ?? []).find((c) => c.kind === k.target && !c.readOnly)?.id ?? "");
    setSourceConn(k.source === "csv" ? (csvConn?.id ?? "") : ((conns.data ?? []).find((c) => c.kind === k.source)?.id ?? ""));
    setName(k.name);
  };

  const onUpload = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    setUploadPct(0);
    try {
      const r = await uploadCsv(ws, f, setUploadPct);
      await qc.invalidateQueries({ queryKey: ["files", ws] });
      await qc.invalidateQueries({ queryKey: ["connections", ws] });
      setFileId(r.file.id);
      setSourceConn(r.connectionId);
      const top = r.suggestions[0];
      const k = top && kits.find((x) => x.id === top.kitId);
      if (k && !kit) pickKit(k, top.entity);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setUploadPct(null);
    }
  };

  const create = async () => {
    if (!kit) return;
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ migration: Migration }>(`/api/workspaces/${ws}/migrations`, {
        name,
        kitId: kit.id,
        entity,
        sourceConnectionId: kit.source === "csv" ? (csvConn?.id ?? sourceConn) : sourceConn,
        sourceObjectId: kit.source === "csv" ? fileId : sourceObj,
        targetConnectionId: targetConn,
      });
      await qc.invalidateQueries({ queryKey: ["migrations", ws] });
      navigate({ to: "/w/$workspaceId/migrations/$migrationId", params: { workspaceId: ws, migrationId: r.migration.id } });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const targets = (conns.data ?? []).filter((c) => kit && c.kind === kit.target);
  const sources = (conns.data ?? []).filter((c) => kit && c.kind === kit.source);
  const ready = kit && name && targetConn && (kit.source === "csv" ? fileId : sourceConn && sourceObj);

  return (
    <>
      <PageHeader title="New migration" subtitle="Nothing is written until you review a dry run and choose to run it." />
      {error && <Alert tone="danger">{error}</Alert>}

      <Card title="1. Choose a kit" className="mb-6">
        {suggestions.length > 0 && !kit && (
          <Alert tone="accent" title="Suggested from your file's columns">
            {suggestions.slice(0, 3).map((s) => {
              const k = kits.find((x) => x.id === s.kitId);
              return (
                k && (
                  <button key={`${s.kitId}-${s.entity}`} className="text-accent mr-4 underline" onClick={() => pickKit(k, s.entity)}>
                    {k.name} · {k.entities.find((e) => e.entity === s.entity)?.label} ({Math.round(s.score * 100)}% match)
                  </button>
                )
              );
            })}
          </Alert>
        )}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {kits.map((k) => (
            <button
              key={k.id}
              onClick={() => pickKit(k)}
              aria-pressed={kit?.id === k.id}
              className={`focus-ring rounded-lg border p-3 text-left ${kit?.id === k.id ? "border-accent bg-accent-soft" : "border-default hover:bg-[var(--surface-2)]"}`}
            >
              <div className="font-medium">{k.name}</div>
              <div className="muted mt-1 text-xs">{k.description}</div>
              <div className="mt-2 flex gap-1">
                <Badge>{KIND_LABELS[k.source]}</Badge>
                <Badge tone="accent">→ {KIND_LABELS[k.target]}</Badge>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {kit && (
        <Card title="2. Source and target" className="mb-6">
          {kit.entities.length > 1 && (
            <Field label="What are you migrating?" htmlFor="entity">
              <Select id="entity" value={entity} onChange={(e) => setEntity(e.target.value)}>
                {kit.entities.map((e) => (
                  <option key={e.entity} value={e.entity}>
                    {e.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {kit.entities.find((e) => e.entity === entity)?.notes && <Alert tone="accent">{kit.entities.find((e) => e.entity === entity)!.notes}</Alert>}

          {kit.source === "csv" ? (
            <Field label="Source file" htmlFor="file" hint={`Expected columns: ${kit.entities.find((e) => e.entity === entity)?.expectedColumns.join(", ")}`}>
              <div className="flex gap-2">
                <Select id="file" value={fileId} onChange={(e) => setFileId(e.target.value)}>
                  <option value="">Choose an uploaded file…</option>
                  {readyFiles.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({formatBytes(f.size)})
                    </option>
                  ))}
                </Select>
                <input ref={fileInput} type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
                <Button variant="secondary" onClick={() => fileInput.current?.click()} loading={uploadPct !== null}>
                  {uploadPct !== null ? `${Math.round(uploadPct * 100)}%` : "Upload"}
                </Button>
              </div>
            </Field>
          ) : (
            <>
              <Field label={`${KIND_LABELS[kit.source]} connection`} htmlFor="src">
                <Select id="src" value={sourceConn} onChange={(e) => setSourceConn(e.target.value)}>
                  <option value="">Choose…</option>
                  {sources.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {!sources.length && (
                <Alert>
                  No {KIND_LABELS[kit.source]} connection yet.{" "}
                  <Link to="/w/$workspaceId/connections" params={{ workspaceId: ws }} className="underline">
                    Add one
                  </Link>
                  .
                </Alert>
              )}
              {sourceConn && (
                <Field label={kit.source === "mailchimp" ? "Audience" : "Object"} htmlFor="obj">
                  {objects.isLoading ? (
                    <Loading />
                  ) : (
                    <Select id="obj" value={sourceObj} onChange={(e) => setSourceObj(e.target.value)}>
                      <option value="">Choose…</option>
                      {(objects.data ?? []).map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                          {o.count !== undefined ? ` (${o.count.toLocaleString()})` : ""}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              )}
            </>
          )}

          <Field label={`${KIND_LABELS[kit.target]} connection (target)`} htmlFor="tgt">
            <Select id="tgt" value={targetConn} onChange={(e) => setTargetConn(e.target.value)}>
              <option value="">Choose…</option>
              {targets.map((c) => (
                <option key={c.id} value={c.id} disabled={c.readOnly}>
                  {c.name}
                  {c.readOnly ? " (read-only)" : ""}
                </option>
              ))}
            </Select>
          </Field>
          {!targets.length && (
            <Alert>
              No {KIND_LABELS[kit.target]} connection yet.{" "}
              <Link to="/w/$workspaceId/connections" params={{ workspaceId: ws }} className="underline">
                Add one
              </Link>
              .
            </Alert>
          )}
          <Field label="Migration name" htmlFor="mname">
            <Input id="mname" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Button onClick={create} disabled={!ready} loading={busy}>
            Create and review mapping
          </Button>
        </Card>
      )}
    </>
  );
}
